import { CalendarClock, Check, ChevronLeft, ChevronRight, ListChecks, PencilLine, RotateCcw, X } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  CRITERION_MARK_SYMBOLS,
  CV_SCAN_MESSAGE,
  RANK_TAB_LABELS,
  jevEvidenceOf,
  type CvDateNote,
  type IcpItem,
  type IcpRanked,
  type JevAskPart,
  type JevPersonResult,
  type RankedCriterion,
  type RankingDetail,
  type RankingRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { CvFileBar, CvViewer, cvMetaLine } from '../../../components/cv/CvViewer';
import { Drawer } from '../../../components/Drawer';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { ServerErrorState } from '../../../components/system-states/SystemState';
import { useCanEdit } from '../../../stores/session';
import { ModuleGate } from '../../../components/ModuleGate';
import { useModule } from '../../../lib/modules';
import { RemarksPanel } from '../../remarks/components/RemarksPanel';
import { useJevPerson } from '../api/jev-api';
import { useCorrectCriterion, useRankingDetail } from '../api/ranking-api';
import { isWaitingForReview } from '../ranking-view';
import { IcpWhy } from './icp/IcpWhy';
import { JevWhy } from './JevMatch';
import { MarkGlyph } from './MarkGlyph';
import styles from './ranking.module.css';

// R5 — RNK-02 "Why this score": the per-criterion breakdown beside the CV, the evidence lines
// marked in it, and a person's ✓/✗ per criterion (F23.05). Opens from RNK-01 (and from the
// review card in R6). The drawer at its wide size (design card "Ranking"); Esc closes it and
// focus goes back to the row.

interface WhyThisScoreProps {
  readonly applicationId: string | null;
  readonly jobId: string;
  readonly onClose: () => void;
  /** Present only for someone who may decide; the Shortlist button and who is already shortlisted. */
  readonly review?: {
    readonly row: RankingRow;
    readonly isMoving: boolean;
    readonly onShortlist: () => void;
  };
  /** ADR 0050 §5, ADR 0051 §6: what Jev found for this person in the ask on screen. */
  readonly jev?: {
    readonly askId: string;
    readonly parts: readonly JevAskPart[];
    readonly result: JevPersonResult | undefined;
    readonly place: number | null;
    readonly isPractice: boolean;
  };
  /**
   * ADR 0062: where this person stands for the ideal candidate profile, line by line. It comes first in the sheet when the
   * profile is the job's main ranking; `ask` names the profile's reading of this person, whose findings (and the lines they answer) are
   * fetched on their own, so the sheet never mixes one ask's findings with another ask's lines.
   */
  readonly icp?: {
    readonly items: readonly IcpItem[];
    readonly ranked: IcpRanked | undefined;
    /** §L8: the knockout penalty already taken off the match. */
    readonly penalty: number;
    readonly place: number | null;
    /** The ask that read this person; null while nobody has read them. */
    readonly ask: { readonly id: string } | null;
  };
  /** Redesign Group 3: Previous / "N of M" / Next through the rows on screen, in the order shown. */
  readonly steps?: SheetSteps;
}

export interface SheetSteps {
  readonly position: number;
  readonly total: number;
  /** Null at the first row. */
  readonly onPrevious: (() => void) | null;
  /** Null at the last row. */
  readonly onNext: (() => void) | null;
}

export function WhyThisScore({ applicationId, jobId, onClose, review, jev, icp, steps }: WhyThisScoreProps): ReactNode {
  const detail = useRankingDetail(applicationId);
  const data = detail.data;
  return (
    <Drawer
      isOpen={applicationId !== null}
      onClose={onClose}
      size="wide"
      title={data?.name ?? 'Why this score'}
      meta={data ? metaOf(data, icp?.ranked) : undefined}
      headerActions={steps ? <StepButtons steps={steps} /> : undefined}
    >
      {detail.isPending && <SkeletonLines count={8} />}
      {detail.error && <ServerErrorState onRetry={() => void detail.refetch()} />}
      {data && <Breakdown detail={data} jobId={jobId} review={review} jev={jev} icp={icp} />}
    </Drawer>
  );
}

/** `[` and `]` step while the sheet is open, as the key hints say; never while typing in it. */
const NO_PARTS: readonly JevAskPart[] = [];
const STEP_KEYS: Readonly<Record<string, 'previous' | 'next'>> = { '[': 'previous', ']': 'next' };

function StepButtons({ steps }: { readonly steps: SheetSteps }): ReactNode {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const direction = STEP_KEYS[event.key];
      if (!direction || event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target) || document.querySelector('[data-popover]')) return;
      const step = direction === 'previous' ? steps.onPrevious : steps.onNext;
      if (!step) return;
      event.preventDefault();
      step();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [steps]);
  return (
    <>
      <Button size="sm" variant="outline" icon={ChevronLeft} aria-label="Previous candidate" title="Previous candidate ( [ )" disabled={steps.onPrevious === null} onClick={() => steps.onPrevious?.()} />
      <span className={styles.sheetPosition} aria-live="polite">
        {steps.position.toLocaleString('en-US')} of {steps.total.toLocaleString('en-US')}
      </span>
      <Button size="sm" variant="outline" icon={ChevronRight} aria-label="Next candidate" title="Next candidate ( ] )" disabled={steps.onNext === null} onClick={() => steps.onNext?.()} />
    </>
  );
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

function metaOf(detail: RankingDetail, ranked: IcpRanked | undefined): string {
  if (ranked) return profileMetaOf(ranked);
  if (detail.score === null || detail.tab === null) return 'Not ranked yet';
  return `Score ${detail.score} · ${RANK_TAB_LABELS[detail.tab]} · Read ${detail.coverage ?? 0}% of what you asked for`;
}

function Breakdown({ detail, jobId, review, jev, icp }: { readonly detail: RankingDetail; readonly jobId: string; readonly review?: WhyThisScoreProps['review']; readonly jev?: WhyThisScoreProps['jev']; readonly icp?: WhyThisScoreProps['icp'] }): ReactNode {
  const isShortlisted = review?.row.reviewStatus === 'shortlisted';
  const isCriteriaOn = useModule('screening_criteria');
  // ADR 0062: this one person's findings for the profile's reading, not the whole ask.
  const person = useJevPerson(jobId, icp?.ask?.id, detail.applicationId);
  const profileResult = person.data?.applicationId === detail.applicationId ? person.data : undefined;
  // Jev's quotes for parts it found at least named: the lines are marked in the CV beside the breakdown.
  const jevQuotes = [jev?.result, profileResult].flatMap((result) => (result?.answers ?? []).flatMap((answer) => (answer && jevEvidenceOf(answer.level) !== 'none' ? answer.quotes.map((quote) => quote.text) : [])));
  const canShortlist = review !== undefined && isWaitingForReview(review.row);
  // The criteria's own breakdown is the ranking for a job still ranked by them; for a profile job it is the lens, shown only with the module on.
  const isCriteriaShown = icp === undefined || isCriteriaOn;
  return (
    <div className={styles.sheet}>
      {(isShortlisted || canShortlist) && (
        <div className={styles.sheetReview}>
          {isShortlisted && <Chip tone="success">Shortlisted</Chip>}
          {canShortlist && (
            <Button size="sm" variant="solid" icon={ListChecks} disabled={review.isMoving} onClick={review.onShortlist}>
              Shortlist
            </Button>
          )}
        </div>
      )}
      <div className={styles.sheetSide}>
        {icp && <IcpWhy items={icp.items} ranked={icp.ranked} penalty={icp.penalty} place={icp.place} />}
        {icp?.ask && <JevWhy name={detail.name} jobId={jobId} askId={icp.ask.id} parts={profileResult?.ask.parts ?? NO_PARTS} result={profileResult} place={null} isPractice={profileResult?.ask.isPractice ?? false} isProfile />}
        {jev && (
          <ModuleGate module="jev">
            <JevWhy name={detail.name} jobId={jobId} askId={jev.askId} parts={jev.parts} result={jev.result} place={jev.place} isPractice={jev.isPractice} />
          </ModuleGate>
        )}
        {isCriteriaShown && <ScoreBreakdown detail={detail} jobId={jobId} />}
        <DatesToCheck notes={detail.dateNotes} />
        {/* F23.13 (ADR 0049): the person's remarks, under the breakdown and beside the CV. */}
        <RemarksPanel applicationId={detail.applicationId} jobId={jobId} name={detail.name} />
      </div>
      <section className={styles.sheetCv} aria-label="CV">
        <CvPane detail={detail} highlights={[...(isCriteriaShown ? evidenceWords(detail) : []), ...jevQuotes, ...detail.dateNotes.flatMap((note) => note.lines)]} />
      </section>
    </div>
  );
}

/** The sheet's one line under the name when the profile ranks: "Match 82 · Top fit · covers 90% of the profile". */
function profileMetaOf(ranked: IcpRanked): string {
  if (ranked.state === 'unreadable') return 'Needs a look';
  if (ranked.match === null || ranked.tab === null) return 'Waiting for Jev to read the CV';
  return `Match ${ranked.match} · ${RANK_TAB_LABELS[ranked.tab]} · covers ${ranked.covers}% of the profile`;
}

/** The CV words that decided a criterion, to mark in the CV (RNK-02). */
export function evidenceWords(detail: RankingDetail): readonly string[] {
  return detail.matches.flatMap((match) => (match.source === 'cv' && match.evidence ? [match.evidence.text.slice(match.evidence.start, match.evidence.end)] : []));
}

const DATE_NOTE_WORDS: Readonly<Record<CvDateNote['kind'], (note: CvDateNote) => string>> = {
  overlap: (note) => `Two roles overlap for ${note.months} months (${note.span}). People do hold two jobs — ask how this worked.`,
  backwards: () => 'These dates run backwards — likely a typo; check with them.',
};

/** ADR 0060, F23.20: the app's own date check, a note and never a score; attention, not danger. Gaps are never flagged. */
function DatesToCheck({ notes }: { readonly notes: readonly CvDateNote[] }): ReactNode {
  if (notes.length === 0) return null;
  return (
    <section className={styles.dateNotes} aria-label="Dates to check">
      <h3 className={styles.dateNotesTitle}>
        <CalendarClock size={16} aria-hidden />
        Dates to check
      </h3>
      <ul className={styles.dateNotesList}>
        {notes.map((note) => (
          <li key={note.lines.join('|')}>
            <p className={styles.dateNoteText}>{DATE_NOTE_WORDS[note.kind](note)}</p>
            {note.lines.map((line) => (
              <p key={line} className={styles.dateNoteLine}>
                {line}
              </p>
            ))}
          </li>
        ))}
      </ul>
      <p className={styles.dateNotesFooter}>Worked out by the app from the dates as written. Never part of the score or Jev’s order. Gaps between jobs are never flagged.</p>
    </section>
  );
}

/** RNK-02's breakdown on its own: the sheet's left half, and the review card's panel (R6, SCR-01). */
export function ScoreBreakdown({ detail, jobId }: { readonly detail: RankingDetail; readonly jobId: string }): ReactNode {
  const canCorrect = useCanEdit('application');
  const { correct, saveNote, pendingKey } = useCorrectCriterion(jobId);
  return (
    <section className={styles.sheetBreakdown} aria-label="Why this score">
      <div className={styles.sheetSummary}>
        <Stars value={detail.stars} />
        <span className={styles.sheetScore}>{detail.score ?? '—'}</span>
        {detail.missingMustHaves.length > 0 && <Chip tone="rejected">Missing: {detail.missingMustHaves.join(', ')}</Chip>}
      </div>
      {detail.cv.state === 'coming' ? (
        <p className={styles.sheetNote}>The CV is still coming; marks read from it may change.</p>
      ) : (
        detail.isPending && <p className={styles.sheetNote}>Re-ranking against the latest criteria…</p>
      )}
      {detail.cv.state === 'unreadable' && <p className={styles.sheetNote}>{CV_SCAN_MESSAGE}</p>}
      <ol className={styles.criteria}>
        {detail.matches.map((match) => (
          <CriterionLine
            key={match.key}
            match={match}
            isPending={pendingKey === match.key}
            canCorrect={canCorrect}
            onSet={(mark) => void correct(detail, match.key, mark === null ? { mark: null, shownMark: match.machineMark, shownMatch: match.machineMatch } : { mark, shownMark: mark, shownMatch: mark === 'met' ? 1 : 0 })}
            onNote={(note) => void saveNote(detail, match.key, note)}
          />
        ))}
      </ol>
    </section>
  );
}

function CriterionLine({
  match,
  isPending,
  canCorrect,
  onSet,
  onNote,
}: {
  readonly match: RankedCriterion;
  readonly isPending: boolean;
  readonly canCorrect: boolean;
  readonly onSet: (mark: 'met' | 'not_met' | null) => void;
  readonly onNote: (note: string | null) => void;
}): ReactNode {
  const corrected = match.correction;
  const [isNoting, setNoting] = useState(false);
  return (
    <li className={styles.criterion} data-pending={isPending || undefined}>
      <div className={styles.criterionHead}>
        <MarkGlyph mark={match.mark} isCorrected={corrected !== null} label={match.label} />
        <span className={styles.criterionLabel}>{match.label}</span>
        <span className={styles.criterionImportance}>{CRITERION_IMPORTANCE_LABELS[match.importance]}</span>
        <span className={styles.criterionPoints}>
          {formatPoints(match.points)} of {match.weight}
        </span>
      </div>
      <p className={styles.criterionReason}>
        {corrected ? (
          <>
            Set to {CRITERION_MARK_SYMBOLS[corrected.mark]} by {corrected.byName}
            {corrected.note ? ` — ${corrected.note}` : ''}. The ranking read: {match.reason}.
          </>
        ) : (
          <>{match.reason}.</>
        )}
      </p>
      {match.evidence && match.source !== 'none' && (
        <p className={styles.evidence}>
          <span className={styles.evidenceSource}>{match.source === 'cv' ? 'CV' : 'Answer'}</span>
          {match.evidence.end > match.evidence.start ? (
            <>
              {match.evidence.text.slice(0, match.evidence.start)}
              <mark>{match.evidence.text.slice(match.evidence.start, match.evidence.end)}</mark>
              {match.evidence.text.slice(match.evidence.end)}
            </>
          ) : (
            `“${match.evidence.text}”`
          )}
        </p>
      )}
      {canCorrect && (
        <div className={styles.criterionActions}>
          <Button size="sm" variant={corrected?.mark === 'met' ? 'solid' : 'outline'} icon={Check} aria-pressed={corrected?.mark === 'met'} disabled={isPending} onClick={() => corrected?.mark !== 'met' && onSet('met')}>
            Set ✓
          </Button>
          <Button size="sm" variant={corrected?.mark === 'not_met' ? 'solid' : 'outline'} icon={X} aria-pressed={corrected?.mark === 'not_met'} disabled={isPending} onClick={() => corrected?.mark !== 'not_met' && onSet('not_met')}>
            Set ✗
          </Button>
          {corrected && !isNoting && (
            <Button size="sm" variant="ghost" icon={PencilLine} onClick={() => setNoting(true)}>
              {corrected.note ? 'Edit the note' : 'Add a note'}
            </Button>
          )}
          {corrected && (
            <Button size="sm" variant="ghost" icon={RotateCcw} disabled={isPending} onClick={() => onSet(null)}>
              Use the ranking's mark
            </Button>
          )}
        </div>
      )}
      {canCorrect && corrected && isNoting && (
        <NoteForm
          label={match.label}
          initial={corrected.note ?? ''}
          isWaiting={isPending}
          onCancel={() => setNoting(false)}
          onSave={(note) => {
            setNoting(false);
            if (note !== corrected.note) onNote(note);
          }}
        />
      )}
    </li>
  );
}

const NOTE_MAX = 500;

/** F23.05's optional note on a ✓/✗, one line, saved with the mark (Redesign Group 3). Empty takes the note away. */
function NoteForm({
  label,
  initial,
  isWaiting,
  onSave,
  onCancel,
}: {
  readonly label: string;
  readonly initial: string;
  /** The mark it hangs on is still being saved; the note goes with the version that comes back. */
  readonly isWaiting: boolean;
  readonly onSave: (note: string | null) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [text, setText] = useState(initial);
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const note = text.trim();
    onSave(note === '' ? null : note);
  }
  return (
    <form className={styles.noteForm} onSubmit={submit}>
      <div className={styles.noteInput}>
        <Input aria-label={`A note on ${label}`} placeholder="Why, in a line — e.g. said so on the call" value={text} maxLength={NOTE_MAX} autoFocus onChange={(event) => setText(event.target.value)} />
      </div>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="sm" variant="solid" type="submit" disabled={isWaiting}>
        Save note
      </Button>
    </form>
  );
}

/**
 * The CV the ranking read, its deciding words marked: a PDF in the viewer, a Word CV as its text on a page under the
 * same bar (also SCR-01's left side, R6). A CV that couldn't be read still shows its scanned page under the note, so
 * it can be read and marked by hand (Redesign Group 3: the mockup's empty box loses to that rule).
 */
export function CvPane({ detail, highlights }: { readonly detail: RankingDetail; readonly highlights: readonly string[] }): ReactNode {
  const { cv } = detail;
  if (cv.kind === 'word' && cv.text) {
    return (
      <div className={styles.cvPane}>
        {cv.documentId && cv.fileName && <CvFileBar documentId={cv.documentId} fileName={cv.fileName} meta={cvMetaLine('Word', cv.sizeBytes)} />}
        <MarkedText text={cv.text} words={highlights} name={detail.name} />
      </div>
    );
  }
  const file = cv.documentId && cv.fileName ? { documentId: cv.documentId, fileName: cv.fileName, kind: cv.kind, isRemoved: false } : null;
  const isComing = cv.state === 'coming';
  return (
    <div className={styles.cvPane}>
      {cv.state === 'unreadable' && (
        <p className={styles.cvUnreadable} role="note">
          <span className={styles.cvUnreadableTitle}>This CV couldn't be read.</span> {CV_SCAN_MESSAGE}
        </p>
      )}
      <CvViewer
        cv={file}
        candidateName={detail.name}
        emptyTitle={isComing ? 'The CV is on its way' : 'No CV'}
        fallback={isComing ? 'It is still downloading.' : 'Set the criteria from the answers.'}
        highlights={highlights}
        sizeBytes={cv.sizeBytes}
      />
    </div>
  );
}

/** A Word CV's text as the ranking read it, on a page, its evidence words marked (handoff R5). */
function MarkedText({ text, words, name }: { readonly text: string; readonly words: readonly string[]; readonly name: string }): ReactNode {
  const pattern = words.length > 0 ? new RegExp(`(${words.map(escapeRegExp).join('|')})`, 'gi') : null;
  const parts = pattern ? text.split(pattern) : [text];
  return (
    <div className={styles.cvText} tabIndex={0} aria-label="CV text">
      <article className={styles.paper} aria-label={`CV of ${name}`}>
        {parts.map((part, index) => (index % 2 === 1 ? <mark key={index}>{part}</mark> : <span key={index}>{part}</span>))}
      </article>
    </div>
  );
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatPoints(points: number): string {
  return Number.isInteger(points) ? String(points) : points.toFixed(1);
}
